import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API } from "../lib/appCommon";

// Compact header light showing live LLM engine health from /api/_internal/llm-stats.
export default function LlmStatusLight() {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/_internal/llm-stats`);
      setStats(r.data);
      setErr(false);
    } catch {
      setErr(true);
    }
  }, []);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 20000);
    return () => clearInterval(timer.current);
  }, [load]);

  const cfg = stats?.config || {};
  const breaker = stats?.breaker || {};
  const lat = stats?.latency_ms || {};
  const calls = stats?.total_calls || 0;
  const errors = stats?.total_errors || 0;
  const successRate = calls > 0 ? Math.round(((calls - errors) / calls) * 100) : null;
  const totalCost = stats?.total_cost_usd;
  const billingCapHits = stats?.total_billing_cap_hits || 0;
  const topModel = stats?.per_model
    ? Object.entries(stats.per_model).sort((a, b) => (b[1]?.cost_usd || 0) - (a[1]?.cost_usd || 0))[0]
    : null;
  const agentHealth = stats?.agent_health || null;
  const worstOutlier = agentHealth?.outliers?.[0] || null;

  // Status color
  let tone = "green", label = "LLM";
  if (err || !cfg.key_configured) { tone = "red"; label = "LLM"; }
  else if (breaker.open) { tone = "red"; }
  else if (successRate != null && successRate < 70) { tone = "amber"; }
  else if ((lat.p95 != null && lat.p95 > 30000)) { tone = "amber"; }
  // A single struggling agent can hide inside a healthy blended average —
  // flag it even when the overall success rate/latency look fine.
  else if (agentHealth?.outliers?.length > 0) { tone = "amber"; }

  const toneColor = tone === "green" ? "#4ade80" : tone === "amber" ? "#fbbf24" : "#f87171";
  const providerLabel = cfg.active_provider === "emergent" ? "Universal (Claude)"
    : cfg.active_provider === "openrouter" ? "OpenRouter"
    : "None";
  const modelLabel = cfg.active_provider === "emergent" ? cfg.default_model : cfg.openrouter_model;
  const popoverSurface = "var(--surface)";

  return (
    <div
      data-testid="llm-status-light"
      style={{ position: "relative", display: "flex", alignItems: "center" }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid="llm-status-pill"
        onClick={() => { setOpen(o => !o); load(); }}
        style={{
          display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
          background: "var(--layer2)", border: "1px solid var(--border)",
          borderRadius: 20, padding: "4px 10px", height: 28,
        }}
        title="LLM engine status"
      >
        <span style={{
          width: 8, height: 8, borderRadius: "50%", background: toneColor, flexShrink: 0,
          boxShadow: `0 0 6px ${toneColor}`,
          animation: tone === "green" ? "llmpulse 2.2s ease-in-out infinite" : "none",
        }} />
        <span style={{ fontSize: 10, fontWeight: 700, color: "var(--text)", letterSpacing: 0.4 }}>{label}</span>
      </button>

      {open && (
        <div
          data-testid="llm-status-popover"
          style={{
            position: "absolute", top: 34, right: 0, zIndex: 3000, width: 232,
            background: popoverSurface,
            border: "1px solid color-mix(in srgb, var(--border), black 18%)",
            borderRadius: 12,
            padding: "12px 14px",
            boxShadow: "0 16px 40px rgba(0,0,0,0.45)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            isolation: "isolate",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 800, color: "var(--text)", letterSpacing: 0.5 }}>LLM ENGINE</span>
            <span style={{
              fontSize: 9, fontWeight: 800, padding: "2px 8px", borderRadius: 20,
              background: `${toneColor}18`, color: toneColor, border: `1px solid ${toneColor}44`,
            }}>
              {err || !cfg.key_configured ? "OFFLINE" : breaker.open ? "BREAKER OPEN" : tone === "amber" ? "DEGRADED" : "HEALTHY"}
            </span>
          </div>

          <Row label="Provider" value={providerLabel} />
          <Row label="Model" value={modelLabel || "—"} mono />
          <Row label="Fallback" value={cfg.openrouter_key_set ? (cfg.active_provider === "emergent" ? "OpenRouter" : "Universal") : "—"} />
          <div style={{ height: 1, background: "var(--border)", margin: "8px 0" }} />
          <Row label="Success rate" value={successRate != null ? `${successRate}%` : "—"} valueColor={successRate != null && successRate < 70 ? "#fbbf24" : "#4ade80"} />
          <Row label="Latency p50" value={lat.p50 != null ? `${Math.round(lat.p50)} ms` : "—"} />
          <Row label="Latency p95" value={lat.p95 != null ? `${Math.round(lat.p95)} ms` : "—"} />
          <Row label="Calls" value={`${calls} · ${errors} err`} />
          {billingCapHits > 0 && (
            <Row label="Billing cap hits" value={String(billingCapHits)} valueColor="#f87171" />
          )}
          <div style={{ height: 1, background: "var(--border)", margin: "8px 0" }} />
          <Row label="Total call cost" value={totalCost != null ? `$${totalCost.toFixed(4)}` : "—"} valueColor="#4ade80" />
          {topModel && (
            <Row label="Top model cost" value={`${topModel[0].split("/").pop()} · $${(topModel[1]?.cost_usd || 0).toFixed(4)}`} mono />
          )}
          {agentHealth && agentHealth.agents_total > 0 && (
            <>
              <div style={{ height: 1, background: "var(--border)", margin: "8px 0" }} />
              <Row
                label="Agents"
                value={`${agentHealth.agents_healthy}/${agentHealth.agents_total} healthy`}
                valueColor={agentHealth.outliers.length > 0 ? "#fbbf24" : "#4ade80"}
              />
              {worstOutlier && (
                <div style={{ marginTop: 4, fontSize: 9, color: "#fbbf24" }}>
                  {worstOutlier.agent_key}: {worstOutlier.success_rate}% ({worstOutlier.fails_in_window} of last {worstOutlier.window} failed)
                </div>
              )}
            </>
          )}
          {breaker.open && (
            <div style={{ marginTop: 8, fontSize: 9, color: "#f87171" }}>
              Circuit breaker tripped — retrying in ~{breaker.open_duration_sec || 90}s.
            </div>
          )}
        </div>
      )}
      <style>{`@keyframes llmpulse { 0%,100%{opacity:1} 50%{opacity:0.45} }`}</style>
    </div>
  );
}

function Row({ label, value, mono, valueColor }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2px 0" }}>
      <span style={{ fontSize: 10, color: "var(--muted)" }}>{label}</span>
      <span style={{ fontSize: 10, fontWeight: 700, color: valueColor || "var(--text)", fontFamily: mono ? "ui-monospace, monospace" : "inherit", maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</span>
    </div>
  );
}
